import type { Application, Endpoints } from "../api";
import { applicationTypeLabel, formatDate, initials } from "../presentation";
import type { ConsoleView } from "../routes";
import { ApplicationStatusBadge } from "./ApplicationStatusBadge";
import { Button } from "./ui/Button";
import { CopyField } from "./ui/CopyField";
import { Icon } from "./ui/Icon";

/** 应用工作台首页，汇总已有配置并提供面向任务的下一步入口。 */
export function ApplicationOverviewView({ application, endpoints, onEdit, onNavigate }: {
  readonly application: Application;
  readonly endpoints: Endpoints | null;
  readonly onEdit: () => void;
  readonly onNavigate: (view: ConsoleView) => void;
}) {
  const native = application.appType === "native";
  const identityReady = native ? application.credential.configured : application.allowedOrigins.length > 0;
  const steps = [
    { complete: application.status === "active", label: "应用已完成配置同步", action: "app-settings" as const },
    { complete: identityReady, label: native ? "AppSecret 已生成" : "Web Origin 已配置", action: "credentials" as const },
    { complete: false, label: "完成 SDK 初始化与登录验证", action: "quickstart" as const },
  ];

  return (
    <div className="app-overview-page">
      <section className="page-heading app-overview-heading">
        <div className="app-title-block">
          <span className={`application-avatar application-avatar-large type-${application.appType}`}>{initials(application.name)}</span>
          <div><p className="breadcrumb"><span>应用中心</span><Icon name="chevron-right" size={14} />应用概览</p><h2>{application.name}</h2><p>{application.description ?? "暂无应用描述"}</p></div>
        </div>
        <div className="page-heading-actions"><ApplicationStatusBadge status={application.status} /><Button onClick={onEdit}>编辑应用</Button><Button leadingIcon="code" variant="primary" onClick={() => { onNavigate("quickstart"); }}>开始接入</Button></div>
      </section>

      {application.lastError === null ? null : <div className="inline-alert app-overview-error"><strong>应用配置同步异常</strong><p>{application.lastError}</p></div>}

      <div className="app-overview-grid">
        <section className="surface-card overview-identity-card">
          <header><div><p className="eyebrow">APPLICATION IDENTITY</p><h3>应用身份</h3></div><span className="management-tag tag-info">{applicationTypeLabel(application.appType)}</span></header>
          <div className="copy-field-grid"><CopyField label="AppKey" value={application.appKey} /><CopyField label="Tenant ID" value={application.tenantId} /><CopyField label="REST API" value={endpoints?.api ?? "加载中…"} /><CopyField label="WebSocket" value={endpoints?.webSocket ?? "加载中…"} /></div>
          <footer><span>配置版本 v{application.version}</span><span>更新于 {formatDate(application.updatedAt)}</span></footer>
        </section>

        <aside className="surface-card onboarding-checklist">
          <header><h3>接入检查清单</h3><p>完成关键配置后再接入生产客户端</p></header>
          {steps.map((step) => <button key={step.label} type="button" onClick={() => { onNavigate(step.action); }}><span className={step.complete ? "complete" : "pending"}><Icon name={step.complete ? "check" : "chevron-right"} size={15} /></span><strong>{step.label}</strong><small>{step.complete ? "已完成" : "继续配置"}</small></button>)}
        </aside>
      </div>

      <section className="quick-action-grid" aria-label="常用操作">
        <QuickAction icon="settings" title="基础配置" description="管理平台类型和接入边界" onClick={() => { onNavigate("app-settings"); }} />
        <QuickAction icon="key" title="凭证与安全" description={native ? "AppSecret、管理员与 UserSig" : "OIDC Public Client 与安全边界"} onClick={() => { onNavigate("credentials"); }} />
        <QuickAction icon="users" title="用户与账号" description="查询 IM 用户和系统账号" onClick={() => { onNavigate("users"); }} />
        <QuickAction icon="server" title="服务端发信" description="使用系统账号发送可信消息" onClick={() => { onNavigate("message-send"); }} />
      </section>
    </div>
  );
}

function QuickAction({ icon, title, description, onClick }: {
  readonly icon: "key" | "server" | "settings" | "users";
  readonly title: string;
  readonly description: string;
  readonly onClick: () => void;
}) {
  return <button className="surface-card quick-action" type="button" onClick={onClick}><span><Icon name={icon} /></span><div><strong>{title}</strong><small>{description}</small></div><Icon name="chevron-right" size={16} /></button>;
}
